import { useCallback, useEffect, useState } from "react";
import {
  ArrowLeft, Loader2, Send, RotateCcw, Film, Clock, CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Textarea } from "../ui/textarea";
import { AttachmentPicker, AttachmentLinks } from "./AttachmentPicker";
import {
  STATUS_STYLE, SEVERITY_STYLE, fmtDate, fmtDuration, fmtClock,
} from "../../lib/support";

export const TicketThread = ({ ticketId, onBack }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/support/tickets/${ticketId}`);
      setTicket(data);
    } catch (err) {
      toast.error(err?.response?.status === 404 ? "Ticket not found" : "Could not load ticket");
      setTicket(null);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  const sendInfo = async () => {
    if (reply.trim().length < 1) return toast.error("Write something first");
    setBusy(true);
    try {
      await api.post(`/support/tickets/${ticketId}/messages`, {
        body: reply.trim(),
        attachment_ids: attachments.map((a) => a.attachment_id),
      });
      setReply(""); setAttachments([]);
      toast.success("Added to your ticket");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally {
      setBusy(false);
    }
  };

  const confirmResolution = async (resolved) => {
    setBusy(true);
    try {
      await api.post(`/support/tickets/${ticketId}/resolution`, {
        resolved,
        note: resolved ? null : (reply.trim() || null),
      });
      if (!resolved) setReply("");
      toast[resolved ? "success" : "warning"](
        resolved ? "Thanks for confirming" : "Ticket reopened"
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      await api.post(`/support/tickets/${ticketId}/reopen`, {
        reason: reply.trim() || null,
      });
      setReply("");
      toast.warning("Ticket reopened");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reopen");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="border border-zinc-800 bg-zinc-900/40 p-12 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="border border-zinc-800 bg-zinc-900/40 p-10 text-center" data-testid="ticket-not-found">
        <div className="text-white font-semibold">That ticket isn't available</div>
        <button
          type="button"
          onClick={onBack}
          className="mt-4 text-[10px] font-mono uppercase tracking-widest border border-zinc-800 hover:bg-zinc-900 text-zinc-300 px-4 py-2"
        >
          Back to my tickets
        </button>
      </div>
    );
  }

  const awaitingConfirmation =
    ticket.status === "resolved" && !ticket.resolution_confirmed?.resolved;

  return (
    <div className="space-y-6" data-testid="ticket-thread">
      <button
        type="button"
        onClick={onBack}
        data-testid="ticket-back-btn"
        className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-3 h-3" /> My tickets
      </button>

      <div className="border border-zinc-800 bg-zinc-900/40">
        <div className="p-6 md:p-8 border-b border-zinc-800">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="font-mono text-sm text-white" data-testid="thread-ticket-id">
              {ticket.ticket_id}
            </span>
            <span
              data-testid="thread-status"
              className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 ${STATUS_STYLE[ticket.status] || ""}`}
            >
              {ticket.status_label}
            </span>
            <span
              className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 ${SEVERITY_STYLE[ticket.reported_severity] || ""}`}
              title="Severity as you reported it"
            >
              {ticket.reported_severity_label} · as reported
            </span>
            <span className="text-[10px] font-mono text-zinc-600 ml-auto">
              Opened {fmtDate(ticket.created_at)}
            </span>
          </div>
          <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-white">
            {ticket.category_label}
          </h2>
        </div>

        {ticket.job && (
          <div className="px-6 md:px-8 py-5 border-b border-zinc-800 bg-zinc-950/40" data-testid="thread-job-context">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
              <Film className="w-3 h-3" /> Related video
            </div>
            <div className="text-sm text-white truncate">{ticket.job.video_name}</div>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[11px] font-mono text-zinc-500">
              <span>{ticket.job.source_language} → {ticket.job.target_language}</span>
              <span>{fmtDuration(ticket.job.duration_seconds)}</span>
              <span>{fmtDate(ticket.job.processing_date)}</span>
              <span className="text-zinc-400">{ticket.job.job_status}</span>
            </div>
            {ticket.cue && (
              <div className="mt-4 border-l-2 border-blue-500/50 pl-4 space-y-1" data-testid="thread-cue-context">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-blue-300">
                  <Clock className="w-3 h-3" />
                  Line #{String(ticket.cue.cue_index).padStart(2, "0")} ·{" "}
                  {fmtClock(ticket.cue.start_seconds)} → {fmtClock(ticket.cue.end_seconds)}
                </div>
                <div className="text-xs font-mono text-zinc-500">{ticket.cue.source_text}</div>
                <div className="text-sm text-white">{ticket.cue.generated_text}</div>
              </div>
            )}
          </div>
        )}

        <div className="p-6 md:p-8 space-y-5">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
              What you reported
            </div>
            <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap" data-testid="thread-description">
              {ticket.description}
            </p>
          </div>
          {ticket.expected_result && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
                What you expected
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
                {ticket.expected_result}
              </p>
            </div>
          )}
          {ticket.attachments?.length > 0 && (
            <AttachmentLinks items={ticket.attachments} testId="thread-attachment" />
          )}
        </div>
      </div>

      {ticket.messages?.length > 0 && (
        <div className="space-y-3" data-testid="thread-messages">
          {ticket.messages.map((m) => (
            <div
              key={m.message_id}
              data-testid={`thread-message-${m.message_id}`}
              className={`border p-5 ${
                m.author === "support"
                  ? "border-blue-500/30 bg-blue-500/5"
                  : "border-zinc-800 bg-zinc-900/40"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-[10px] font-mono uppercase tracking-widest ${
                  m.author === "support" ? "text-blue-300" : "text-zinc-400"
                }`}>
                  {m.author === "support" ? "LinguaSub Support" : "You"}
                </span>
                <span className="text-[10px] font-mono text-zinc-600 ml-auto">
                  {fmtDate(m.created_at)}
                </span>
              </div>
              <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{m.body}</p>
              {m.attachments?.length > 0 && (
                <div className="mt-3">
                  <AttachmentLinks items={m.attachments} testId="msg-attachment" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {awaitingConfirmation && (
        <div className="border border-emerald-500/30 bg-emerald-500/5 p-6" data-testid="resolution-prompt">
          <div className="font-heading text-lg font-bold tracking-tight text-white">
            Was this problem resolved?
          </div>
          <p className="text-xs text-zinc-400 mt-1">
            Answering no reopens the ticket — add anything extra below first if
            you want it attached.
          </p>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={() => confirmResolution(true)}
              disabled={busy}
              data-testid="resolution-yes-btn"
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest bg-emerald-500 hover:bg-emerald-400 text-zinc-950 disabled:opacity-50 px-5 py-2.5 transition-colors"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Yes
            </button>
            <button
              type="button"
              onClick={() => confirmResolution(false)}
              disabled={busy}
              data-testid="resolution-no-btn"
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest border border-red-500/40 text-red-300 hover:bg-red-500/10 disabled:opacity-50 px-5 py-2.5 transition-colors"
            >
              <XCircle className="w-3.5 h-3.5" /> No, reopen it
            </button>
          </div>
        </div>
      )}

      <div className="border border-zinc-800 bg-zinc-900/40 p-6 space-y-4" data-testid="thread-reply-box">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          Add more information
        </div>
        <Textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={4}
          maxLength={5000}
          data-testid="thread-reply-input"
          placeholder="Anything that helps — a timestamp, a second example, what you tried…"
          className="bg-zinc-950 border-zinc-800 rounded-none text-white placeholder:text-zinc-600 resize-none"
        />
        <AttachmentPicker value={attachments} onChange={setAttachments} testId="thread-attachment-picker" />
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={sendInfo}
            disabled={busy}
            data-testid="thread-send-btn"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-5 py-2.5 transition-colors"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send
          </button>
          {(ticket.status === "resolved" || ticket.status === "reopened") && (
            <button
              type="button"
              onClick={reopen}
              disabled={busy}
              data-testid="thread-reopen-btn"
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest border border-zinc-800 hover:bg-zinc-900 text-zinc-300 disabled:opacity-50 px-5 py-2.5 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reopen ticket
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
